import {createHash} from '@s-ui/js/lib/hash'

import {checkEmailLeadEvent, checkPhoneLeadEvent, getAnonymousId} from '../../../utils.js'

export const UNKNOWN_USER = 'unknown'
export const DEFAULT_PAGE_EVENT = 'placebo'

export const HOME_VIEWED_EVENT_NAME = 'Home Viewed'
export const LIST_VIEWED_EVENT_NAME = 'List Viewed'
export const DETAIL_VIEWED_EVENT_NAME = 'Detail Viewed'
export const AD_FAVORITED_EVENT_NAME = 'Ad Favorited'
export const ALERT_CREATED_EVENT_NAME = 'Alert Created'
export const LEAD_TRIGGERED_EVENT_NAME = 'Lead Triggered'
export const OFFER_TYPE_HOME_CAROUSEL_CLICKED_EVENT_NAME = 'Offer Type Home Carousel Clicked'

const getAdIds = ({properties}) => {
  const {products} = properties
  const maxNumberOfAdsToTrack = 5

  return (
    products?.length &&
    products.slice(0, maxNumberOfAdsToTrack).map(product => {
      return product.id?.toString()
    })
  )
}

const getLeadType = ({event, properties}) => {
  const LEAD_TYPE_MESSAGE = 'message'
  const LEAD_TYPE_PHONE = 'phone'
  const LEAD_TYPE_UNKNOWN = 'unknown'

  const isEmailLeadEvent = checkEmailLeadEvent({event, properties})
  const isPhoneLeadEvent = checkPhoneLeadEvent({event, properties})

  if (isEmailLeadEvent) return LEAD_TYPE_MESSAGE
  if (isPhoneLeadEvent) return LEAD_TYPE_PHONE

  return LEAD_TYPE_UNKNOWN
}

const getSellerType = ({properties}) => {
  const {seller_type: sellerType} = properties

  const PROFESSIONAL_SELLER_TYPE = 'profesional'
  const PRIVATE_SELLER_TYPE = 'particular'
  const UNKNOWN_SELLER_TYPE = 'unknown'

  const sellerTypes = [PROFESSIONAL_SELLER_TYPE, PRIVATE_SELLER_TYPE]

  return sellerTypes.includes(sellerType) ? sellerType : UNKNOWN_SELLER_TYPE
}

const getUidEvent = () => {
  const anonymousId = getAnonymousId()
  const hashedId = anonymousId && createHash(anonymousId) // hashing anonymousId to avoid sharing it with third party

  return {
    eventType: 'uid',
    id: hashedId || UNKNOWN_USER
  }
}

const getMainEvent = ({mappedEvent}) => {
  return {
    eventType: mappedEvent
  }
}

const getAdsListEvent = ({mappedEvent, properties}) => {
  const adIds = getAdIds({properties}) || []

  return {
    ...(mappedEvent && getMainEvent({mappedEvent})),
    offerIds: [...adIds]
  }
}

const getAdDetailEvent = ({mappedEvent, properties}) => {
  const {ad_id: adId} = properties || {}

  return {
    ...(mappedEvent && getMainEvent({mappedEvent})),
    offerId: adId
  }
}

const getAdFavoritedEvent = ({mappedEvent, properties}) => {
  const {products} = properties
  const favoriteId = products.length && products[0].id

  return {
    ...(mappedEvent && getMainEvent({mappedEvent})),
    offerId: favoriteId
  }
}

const getAlertCreatedEvent = ({mappedEvent, properties}) => {
  const {hit_id: alertId} = properties

  return {
    ...(mappedEvent && getMainEvent({mappedEvent})),
    offerId: alertId
  }
}

const getOfferTypeHomeCarouselClickedEvent = ({mappedEvent, properties}) => {
  const {hit_information: category} = properties

  return {
    ...(mappedEvent && getMainEvent({mappedEvent})),
    categoryId: category
  }
}

const getLeadTriggeredEvent = ({event, mappedEvent, properties}) => {
  const {price} = properties

  const adIds = getAdIds({properties}) || []
  const sellerType = getSellerType({properties})
  const leadType = getLeadType({event, properties})

  const tmstmp = Date.now()

  const conversionId = `${sellerType}-${leadType}-${tmstmp}`

  return {
    ...(mappedEvent && getMainEvent({mappedEvent})),
    conversionClass: 'order',
    conversionSubClass: 'purchase',
    conversionId,
    offerIds: [...adIds],
    conversionValue: price?.toString(),
    conversionCurrency: 'EUR'
  }
}

const getDefaultPageEvent = () => {
  return {
    eventType: DEFAULT_PAGE_EVENT
  }
}

export const getPixelEvents = ({event, properties, mappedEvent, isPage}) => {
  let pixelEvents

  switch (event) {
    case HOME_VIEWED_EVENT_NAME:
      pixelEvents = [getMainEvent({mappedEvent}), getUidEvent()]
      break
    case LIST_VIEWED_EVENT_NAME:
      pixelEvents = [getAdsListEvent({mappedEvent, properties}), getUidEvent()]
      break
    case DETAIL_VIEWED_EVENT_NAME:
      pixelEvents = [getAdDetailEvent({mappedEvent, properties}), getUidEvent()]
      break
    case AD_FAVORITED_EVENT_NAME:
      pixelEvents = [getAdFavoritedEvent({mappedEvent, properties}), getUidEvent()]
      break
    case ALERT_CREATED_EVENT_NAME:
      pixelEvents = [getAlertCreatedEvent({mappedEvent, properties}), getUidEvent()]
      break
    case LEAD_TRIGGERED_EVENT_NAME:
      pixelEvents = [getLeadTriggeredEvent({event, mappedEvent, properties}), getUidEvent()]
      break
    case OFFER_TYPE_HOME_CAROUSEL_CLICKED_EVENT_NAME:
      pixelEvents = [getOfferTypeHomeCarouselClickedEvent({mappedEvent, properties}), getUidEvent()]
      break
    default:
      if (isPage) pixelEvents = [getDefaultPageEvent(), getUidEvent()]
      break
  }

  return pixelEvents
}
